import React, { useState, useContext, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import EDDivider from '../../../../../../../BaseComponents/Divider/EDDivider';
import { EDTextEdit } from '../../../../../../../BaseComponents/TextEdit/EDTextEdit';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import EDKeybind from '../../../../../../../BaseComponents/Keybinds/EDKeybind';
import {
  useReturnFakturaAttestanter,
  getReturnFakturaDisabledReason,
  attestanterEquals,
  getAttestTypText,
  ReturnToUtredningEnum,
} from './Utils/returnFakturaAttestanterUtils';
import { KonteringContext } from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';

const useStyles = makeStyles()(theme => ({
  dialogContentWrapper: {
    [theme.breakpoints.up('md')]: {
      minWidth: '370px',
    },
  },
  dividerMargin: { margin: `${theme.spacing(1)} 0` },
  dividerMarginBottom: { marginBottom: theme.spacing(1) },
}));

interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const ReturnFakturaDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles();
  const [message, setMessage] = useState('');
  const businessDataFormContext = useContext(BusinessDataFormContext);
  const konteringContext = useContext(KonteringContext);
  const messageInputRef = useRef<HTMLInputElement | null>(null);

  const {
    attestanterToReturnTo,
    addAttestant,
    removeAttestant,
    setAttestanterToReturnTo,
    onEnterKeyDown,
    onCtrlEnterKeyDown,
    onReturnFaktura,
    attestanterSomHarAttesterat,
    autoCheckedAttestanterToReturnTo,
    returnToUtredning,
    setReturnToUtredning,
  } = useReturnFakturaAttestanter(message, messageInputRef, props.setOpen);

  const returnFakturaDisabledReason = getReturnFakturaDisabledReason(
    attestanterToReturnTo,
    message,
    returnToUtredning,
    konteringContext.konteringValidationQueryLoading,
    businessDataFormContext
  );

  return (
    <EDKeybind keymap="Enter" onKeyDown={onEnterKeyDown}>
      <EDKeybind keymap="Enter" ctrl onKeyDown={onCtrlEnterKeyDown}>
        <Dialog
          open={props.open}
          onClose={() => {
            props.setOpen(false);
          }}
          title="Skicka tillbaka faktura"
          footerActions={[
            {
              label: 'Avbryt',
              onClick: () => {
                props.setOpen(false);
              },
            },
            {
              label: businessDataFormContext?.mutationLoading
                ? 'Skickar'
                : 'Skicka',
              onClick: onReturnFaktura,
              buttonProps: {
                disabled:
                  Boolean(returnFakturaDisabledReason) ||
                  businessDataFormContext?.mutationLoading,
                disabledReason: returnFakturaDisabledReason,
              },
            },
          ]}
          classes={{ contentWrapper: classes.dialogContentWrapper }}
          onExited={() => {
            setMessage('');
            setAttestanterToReturnTo([]);
            setReturnToUtredning(null);
          }}
        >
          <EDTypography
            bold
            color={returnToUtredning ? 'textSecondary' : 'textPrimary'}
          >
            Skicka tillbaka till attestant
          </EDTypography>
          {attestanterSomHarAttesterat.map(attestant => {
            const autoChecked = autoCheckedAttestanterToReturnTo.some(
              attestantToReturnTo =>
                attestanterEquals(attestantToReturnTo, attestant)
            );

            return (
              <EDBox key={`${attestant.AttestantSign}-${attestant.AttestTyp}`}>
                <EDCheckbox
                  label={`${getAttestTypText(attestant.AttestTyp)} ${
                    attestant.Attestant
                  }`}
                  onCheck={value => {
                    if (value) {
                      addAttestant(attestant);
                    } else {
                      removeAttestant(attestant);
                    }
                  }}
                  checked={
                    attestanterToReturnTo.some(attestantToReturnTo =>
                      attestanterEquals(attestantToReturnTo, attestant)
                    ) || autoChecked
                  }
                  disabled={autoChecked || Boolean(returnToUtredning)}
                  disabledReason={
                    returnToUtredning
                      ? 'Kan inte skicka tillbaka utredning och attestant samtidigt'
                      : 'Måste attestera på nytt eftersom tidigare attestanter attesterar om'
                  }
                  testId={`return-faktura-${getAttestTypText(
                    attestant.AttestTyp
                  ).toLocaleLowerCase()}`}
                />
              </EDBox>
            );
          })}

          {attestanterSomHarAttesterat.length === 0 && (
            <EDTypography italic>Inga attestanter har attesterat</EDTypography>
          )}
          <EDDivider
            marginY={attestanterSomHarAttesterat.length === 0 ? 1 : undefined}
            marginBottom={
              attestanterSomHarAttesterat.length === 0 ? undefined : 1
            }
          />
          <EDTypography
            bold
            color={
              attestanterToReturnTo.length > 0 ? 'textSecondary' : 'textPrimary'
            }
          >
            Skicka tillbaka för utredning
          </EDTypography>
          <EDBox>
            <EDCheckbox
              label="Skicka utan attest"
              onCheck={value => {
                setReturnToUtredning(
                  value ? ReturnToUtredningEnum.withoutAttest : null
                );
              }}
              checked={
                returnToUtredning === ReturnToUtredningEnum.withoutAttest
              }
              disabled={attestanterToReturnTo.length > 0}
              disabledReason="Kan inte skicka tillbaka utredning och attestant samtidigt"
              testId="return-faktura-without-attest"
            />
          </EDBox>
          <EDBox>
            <EDCheckbox
              label="Attestera och skicka till utredning"
              onCheck={value => {
                setReturnToUtredning(
                  value ? ReturnToUtredningEnum.withAttest : null
                );
              }}
              checked={returnToUtredning === ReturnToUtredningEnum.withAttest}
              disabled={attestanterToReturnTo.length > 0}
              disabledReason="Kan inte skicka tillbaka utredning och attestant samtidigt"
              testId="return-faktura-with-attest"
            />
          </EDBox>
          <EDDivider />
          <EDTextEdit
            label="Meddelande"
            required
            multiline
            value={message}
            onChange={setMessage}
            inputRef={messageInputRef}
            testId="return-faktura-meddelande"
          />
        </Dialog>
      </EDKeybind>
    </EDKeybind>
  );
};

export default ReturnFakturaDialog;
